import React, {useEffect, useMemo} from 'react'
import {Navigate, Outlet, Route, Routes, useLocation, useNavigate} from 'react-router-dom'
/* import ListCourier from '../app/components/courier/List'
import CreateBulkOrder from '../app/components/orders/create/bulkOrder' */

import axios from 'axios'
import {toast} from 'react-toastify'
import store from '../app/redux/store'
import {isAdmin, isAuthorized, isEmailDomainAllowed} from '../app/helpers/ReduxHelpers'
import {isConfigRouteVisible} from '../app/helpers/configVisibility'
import {apiConstants} from '../app/common/constants'
import {useSelector} from 'react-redux'
import {updateToken} from '../app/redux/userSlice'
import VerticalSuggestionBanner from '../app/components/home/VerticalSuggestionBanner'
import {clearSessionAndRedirect} from '../app/helpers/sessionHelper'
import {logout, setSelectedMerchantOid} from '../app/redux/userSlice'
import {commonRTK} from '../app/redux/commonRTK'
import {commonRTKPersist} from '../app/redux/commonRTKPersist'

import Sidebar from '../app/menu/Sidebar'
import TopBar from '../app/menu/TopBar'

import {useSidebarToggle} from '../app/components/SidebarToggleContext'
import {desktopContentMargin, useAutoFitOverflow, useIsMobileFrame, usePhoneFilterToggle} from '../app/theme/responsive.config'

import {ROLE_COMBINATIONS, routesConfig} from './routes.config'
import UnAuthorized from './UnAuthorized'
import {getSessionId, trackError} from '../app/analytics'

axios.interceptors.request.use(
  (config) => {
    const {
      user: {
        userInfo: {accessToken},
        selectedMerchantOid,
      },
    } = store.getState()
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`
    }
    // Cross-account merchant switch (non-admin grant): same token for the whole session,
    // just re-declare which merchant to act as on every call. Absent = home merchant.
    if (selectedMerchantOid) {
      config.headers['X-Merchant-Oid'] = selectedMerchantOid
    }
    // EMS V2 lists and raises tickets for the merchant this panel shows. For Prozo staff that is
    // the merchant they picked, which their token doesn't carry; the server ignores it for
    // merchant logins.
    const shownMerchantOid = store.getState().user.userInfo?.merchant?.id
    if (shownMerchantOid && /(^|\/)escalation\//.test(config.url || '')) {
      config.headers['X-Ems-Merchant-Oid'] = shownMerchantOid
    }
    config.headers['X-Session-Id'] = getSessionId()

    return config
  },
  (err) => Promise.reject(err)
)

let refreshPromise = null

axios.interceptors.response.use(
  (response) => response,
  async (err) => {
    // Track requests that never reached the server (network errors, timeouts)
    // Skip if the failing request is itself a tracking call to avoid infinite loops
    const failedUrl = err.config?.url || ''
    if (!err.response && err.request && !failedUrl.includes('usage-analytics')) {
      trackError({
        sessionId: getSessionId(),
        route: window.location.pathname,
        errorCode: 0,
        errorMessage: err.message || 'Network Error',
        apiEndpoint: failedUrl,
      })
    }

    const msg = err?.response?.data?.message?.toLowerCase() || ''

    // Session killed server-side — the user logged out (possibly in another tab), an admin ran
    // logout-all, or their password changed. Refreshing is pointless: /auth/refreshtoken would
    // be rejected too, since logout drops the refresh session along with the access token. Bail
    // straight to login instead of letting a 401 surface as a broken page.
    const isRevoked = err?.response?.status === 401 && msg.includes('session expired')
    if (isRevoked) {
      clearSessionAndRedirect()
      return Promise.reject(err)
    }

    const isExpired = err?.response?.status === 401 && (msg.includes('jwt') && (msg.includes('expired') || msg.includes('token is expired')))

    // Cross-account merchant switch: grant revoked or target merchant deactivated mid-session.
    // This is NOT a token/auth problem — the JWT is still fully valid — so we only drop the
    // X-Merchant-Oid selection and fall back to the home merchant, then retry once.
    const isMerchantAccessRevoked =
      err?.response?.status === 403 && msg.includes('not authorized for this merchant account')
    if (isMerchantAccessRevoked && !err.config?._merchantRetry) {
      err.config._merchantRetry = true
      store.dispatch(setSelectedMerchantOid(null))
      store.dispatch(commonRTK.util.resetApiState())
      store.dispatch(commonRTKPersist.util.resetApiState())
      toast.error('Access to this merchant account was revoked. Switched back to your home account.')
      delete err.config.headers['X-Merchant-Oid']
      return axios(err.config)
    }

    if (isExpired && !err.config?._retry) {
      err.config._retry = true
      const refreshToken = localStorage.getItem('refreshToken')
      if (!refreshToken) {
        store.dispatch(logout())
        return Promise.reject(err)
      }
      try {
        if (!refreshPromise) {
          refreshPromise = axios
            .post(`${apiConstants.BASE_LOCAL}auth/refreshtoken`, {refreshToken, panel: 'MERCHANT'})
            .finally(() => {
              refreshPromise = null
            })
        }
        const res = await refreshPromise
        const newAccessToken = res.data.accessToken
        const newRefreshToken = res.data.refreshToken
        localStorage.setItem('userToken', newAccessToken)
        if (newRefreshToken) localStorage.setItem('refreshToken', newRefreshToken)
        store.dispatch(updateToken(newAccessToken))
        err.config.headers.Authorization = `Bearer ${newAccessToken}`
        return axios(err.config)
      } catch {
        store.dispatch(logout())
      }
    }
    return Promise.reject(err)
  }
)


function Layout() {
  const {pathname} = useLocation()
  const {userInfo} = useSelector((state) => state.user)
  const {sidebarWidth} = useSidebarToggle()
  const isMobile = useIsMobileFrame()
  useAutoFitOverflow(isMobile, pathname)
  usePhoneFilterToggle(isMobile)
  const [hasRedirected, setHasRedirected] = React.useState(false)
  const navigate = useNavigate()

  useEffect(() => {
    let isActive = true
    const kycStatus = userInfo?.merchant?.status
    if (
      kycStatus === 'KYC_PENDING' &&
      ['/orders', '/b2b-orders'].includes(pathname) &&
      !hasRedirected &&
      isActive
    ) {
      setHasRedirected(true)
      navigate('/profile', {replace: true})
    }
    return () => {
      isActive = false
    }
  }, [pathname, userInfo, hasRedirected, navigate])

  // Desktop: pages sit to the right of the sidebar. On phones and tablets the sidebar is a
  // slide-over menu, so pages use the full width.
  const contentMargin = isMobile ? 0 : desktopContentMargin(sidebarWidth)

  // Single-vertical merchants get a soft cross-sell tip for the vertical they are NOT on, shown at
  // the top of every page (dismissed once per browser). Duals and unflagged merchants (both shown)
  // get nothing. Reuses the same resolved D2C/PTL flags as the sidebar.
  const merchant = userInfo?.merchant
  const showPtl = merchant?.isPTLMerchant !== false
  const showD2c = merchant?.isD2CMerchant !== false
  const suggestVertical = (!showD2c && showPtl) ? 'D2C' : (!showPtl && showD2c) ? 'PTL' : null

  return (
    <div style={{display: 'flex', flexDirection: 'column', minHeight: '100vh'}}>
      <TopBar />
      <div style={{display: 'flex', flex: 1}}>
        <Sidebar />
        <div
          data-route={pathname}
          style={{
            flex: 1,
            marginLeft: contentMargin,
            transition: 'margin-left 0.2s ease-in-out',
            minWidth: 0,
            overflow: 'auto',
            maxWidth: `calc(100vw - ${contentMargin}px)`,
          }}
        >
          {suggestVertical && (
            <div style={{padding: '12px 20px 0'}}>
              <VerticalSuggestionBanner vertical={suggestVertical} />
            </div>
          )}
          <Outlet />
        </div>
      </div>
    </div>
  )
}

const REDIRECT_ROUTES = [
  '/login',
  '/forgot-password',
  '/loginuser',
  '/verify-otp',
  '/reset-password',
  '/signup',
  '/otp',
  '/create-new-password',
  '/toc',
  '/google/redirect_uri/*',
]

export default function PrivateRoutes() {
  const {userInfo} = useSelector((state) => state.user)
  const isPTLMerchant = useMemo(() => userInfo?.merchant?.isPTLMerchant, [userInfo])
  const isD2CMerchant = useMemo(() => userInfo?.merchant?.isD2CMerchant, [userInfo])
  const {orderConversionDetails} = useSelector((state) => state.report)

  let intitalRoute = ''

  if (isD2CMerchant && isAdmin()) {
    intitalRoute = '/orders'
  } else if (isPTLMerchant && isAdmin()) {
    intitalRoute = '/b2b-orders'
  } else if (isPTLMerchant && isAuthorized(ROLE_COMBINATIONS.PTL_ORDERS)) {
    intitalRoute = '/b2b-orders'
  } else if (isAuthorized(ROLE_COMBINATIONS.B2C_ORDERS)) {
    intitalRoute = '/orders'
  } else if (isAuthorized(ROLE_COMBINATIONS.DASHBOARD_ACCESS)) {
    intitalRoute = '/dashboard'
  } else if (isAuthorized(ROLE_COMBINATIONS.REPORTS_ACCESS)) {
    intitalRoute = '/reports'
  } else if (isAuthorized(ROLE_COMBINATIONS.CONTROL_TOWER)) {
    intitalRoute = '/control-tower'
  } else {
    intitalRoute = '/change-password'
  }

  return (
    <Routes>
      <Route element={<Layout />}>
        {REDIRECT_ROUTES.map((route) => (
          <Route exact element={<Navigate to={intitalRoute} />} path={route} />
        ))}

        {routesConfig.map((route) => {
          if (!isAuthorized(route.roles)) return null
          if (!isEmailDomainAllowed(route.allowedEmailDomains)) return null
          if (route.enableForConfig && !isConfigRouteVisible(route.enableForConfig, orderConversionDetails)) return null
          if (!route.menuItems) {
            return <Route exact path={route.pathname} element={<>{route.component}</>} />
          }
          return (
            <Route>
              {route.menuItems
                .filter((item) => isConfigRouteVisible(item.enableForConfig, orderConversionDetails))
                .map((item) => (
                  <Route
                    exact
                    key={item.pathname}
                    path={item.pathname}
                    element={<>{item.component}</>}
                  />
                ))}
            </Route>
          )
        })}

        {/* <Route exact path='/couriers' element={<ListCourier />} /> */}

        {/* <Route exact path='/bulk-order' element={<CreateBulkOrder />} /> */}

        <Route path='/*' element={<UnAuthorized />} />
      </Route>
    </Routes>
  )
}
